import type { WovarProduct } from "@commercekit/api";
import type { ProductGalleryVideo } from "~/lib/youtubeUtils";
import { extractYoutubeVideoIdFromUrl } from "~/lib/youtubeUtils";

export type ProductImage = NonNullable<WovarProduct["images"]>[number];

export type ProductSlide =
  | { kind: "image"; image: ProductImage }
  | { kind: "youtube"; id: string; title: string }
  | { kind: "file"; src: string; title: string };

// order: first image, first video, remaining images, remaining videos.
export function buildProductSlides(
  images: WovarProduct["images"],
  videos: ProductGalleryVideo[] | undefined
): ProductSlide[] {
  const videoSlides: ProductSlide[] = [];
  for (const v of videos ?? []) {
    const url = String(v?.url ?? "").trim();
    if (!url) {
      continue;
    }
    const name = String(v?.name ?? "").trim();
    const title = name || `Video ${videoSlides.length + 1}`;
    const yid = extractYoutubeVideoIdFromUrl(url);
    videoSlides.push(
      yid
        ? { kind: "youtube", id: yid, title }
        : { kind: "file", src: url, title }
    );
  }

  const imageSlides: ProductSlide[] = (images ?? [])
    .filter((image): image is ProductImage => Boolean(image?.urlOriginal))
    .map((image) => ({ kind: "image", image } as const));
  const [firstVideo, ...restVideos] = videoSlides;

  if (!firstVideo) return imageSlides;
  if (imageSlides.length === 0) return videoSlides;

  return [imageSlides[0], firstVideo, ...imageSlides.slice(1), ...restVideos];
}

export function productSlideKey(slide: ProductSlide, index: number): string {
  switch (slide.kind) {
    case "youtube":
      return `yt-${slide.id}`;
    case "file":
      return `file-${index}-${slide.src.length}`;
    default:
      return `img-${index}`;
  }
}

export function productImageSrc(image: ProductImage): string {
  return image.alternativeSizes[3]
    ? image.alternativeSizes[3]?.url || ""
    : image.urlOriginal || "";
}

// Akeneo videos have no poster; `#t=0.1` + preload="metadata" paints that frame instead.
export function withPosterFragment(src: string): string {
  return src.includes("#") ? src : `${src}#t=0.1`;
}
